import React, { useState, useEffect } from "react";
import {
  Box, Card, Chip, Divider, Grid, Stack, Typography,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Autocomplete, CircularProgress,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import StarsIcon from "@mui/icons-material/Stars";
import EditIcon from "@mui/icons-material/Edit";
import {
  useGetSpecializationsQuery,
  useUpdateBeauticianOverviewMutation,
} from "slices/beautyApp/beauticianSlice";
import Swal from "sweetalert2";

export default function OverviewTab({ beautician: b, refetch }) {
  const [editOpen, setEditOpen] = useState(false);
  const [bio, setBio] = useState("");
  const [selectedSpecs, setSelectedSpecs] = useState([]);

  const { data: allSpecs = [] } = useGetSpecializationsQuery();
  const [updateOverview, { isLoading: saving }] = useUpdateBeauticianOverviewMutation();

  useEffect(() => {
    if (editOpen) {
      setBio(b?.bio || "");
      setSelectedSpecs(b?.specializations || []);
    }
  }, [editOpen, b]);

  const handleSave = async () => {
    try {
      await updateOverview({
        id: b?._id,
        bio,
        specializations: selectedSpecs.map((s) => s._id),
      }).unwrap();
      refetch();
      setEditOpen(false);
      Swal.fire({ icon: "success", title: "Updated", text: "Overview updated successfully.", timer: 1500, showConfirmButton: false });
    } catch (e) {
      Swal.fire({ icon: "error", title: "Error", text: e?.data?.message || "Failed to update." });
    }
  };

  return (
    <Box sx={{ maxWidth: 720, margin: "auto", mt: 4, px: 2 }}>
      <Card
        sx={{
          p: 4, borderRadius: 4,
          boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
          backgroundColor: "#fff",
          "&:hover": { boxShadow: "0 4px 16px rgba(0,0,0,0.08)" },
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <PersonIcon color="primary" sx={{ fontSize: 32 }} />
            <Typography variant="h5" fontWeight={600} sx={{ color: "#333" }}>
              Beautician Overview
            </Typography>
          </Stack>
          <IconButton onClick={() => setEditOpen(true)} sx={{ color: "#442141" }}>
            <EditIcon />
          </IconButton>
        </Stack>

        {/* Bio */}
        <Typography variant="h6" gutterBottom fontWeight={600} sx={{ mb: 1 }}>
          Bio
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.6, mb: 4 }}>
          {b?.bio?.trim() || "No bio available."}
        </Typography>

        <Divider sx={{ my: 3, borderColor: "#eee" }} />

        {/* Specializations */}
        <Typography variant="h6" gutterBottom fontWeight={600} sx={{ mb: 2 }}>
          Specializations
        </Typography>
        {b?.specializations?.length > 0 ? (
          <Grid container spacing={1}>
            {b.specializations.map((item, i) => (
              <Grid item key={i}>
                <Chip
                  icon={<StarsIcon sx={{ color: "#757575", fontSize: 18 }} />}
                  label={item.name}
                  variant="outlined"
                  sx={{
                    backgroundColor: "#fafafa", color: "#555", fontWeight: 500,
                    px: 2, py: 0.5, fontSize: 13, borderColor: "#ddd",
                    "& .MuiChip-icon": { color: "#757575" },
                  }}
                />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
            No specializations listed.
          </Typography>
        )}
      </Card>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: "#442141" }}>Edit Overview</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            label="Bio"
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            multiline
            rows={4}
            fullWidth
            sx={{ mb: 3, mt: 1 }}
          />
          <Autocomplete
            multiple
            options={allSpecs}
            getOptionLabel={(o) => o.name}
            value={selectedSpecs}
            onChange={(_, val) => setSelectedSpecs(val)}
            isOptionEqualToValue={(o, v) => o._id === v._id}
            renderInput={(params) => (
              <TextField {...params} label="Specializations" placeholder="Select specializations" />
            )}
            renderTags={(val, getTagProps) =>
              val.map((o, i) => (
                <Chip
                  key={i}
                  label={o.name}
                  size="small"
                  icon={<StarsIcon sx={{ fontSize: 14 }} />}
                  {...getTagProps({ index: i })}
                />
              ))
            }
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditOpen(false)} sx={{ color: "#442141" }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            sx={{ background: "#442141", "&:hover": { background: "#5a2d57" } }}
          >
            {saving ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Update"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
